import { useEffect, useMemo, useRef, useState } from 'react'
import type { Feuillet, LienPage, Zone } from '../lib/types'
import { libelle } from '../lib/plans'

const pct = (v: number) => `${v * 100}%`

/**
 * Couche des liens d'une page (bulles de détails), posée sur le plan.
 *
 * Lecture : chaque bulle reconnue est encadrée en bleu ; un toucher ouvre
 * la page visée. Édition (administrateur) : un toucher choisit le lien à
 * corriger ; en mode « ajout », on trace un rectangle sur une bulle oubliée.
 */
export function CoucheLiens({
  liens,
  edition,
  ajout,
  choisi,
  choisir,
  suivre,
  tracer,
  flash
}: {
  liens: LienPage[]
  edition: boolean
  ajout: boolean
  choisi: string | null
  choisir: (id: string | null) => void
  suivre: (l: LienPage) => void
  tracer: (z: Zone) => void
  flash: { x: number; y: number; cle: number } | null
}) {
  const couche = useRef<HTMLDivElement>(null)
  const depart = useRef<[number, number] | null>(null)
  const doigts = useRef(new Set<number>())
  const brouillonRef = useRef<Zone | null>(null)
  const [brouillon, setBrouillon] = useState<Zone | null>(null)

  useEffect(() => {
    depart.current = null
    brouillonRef.current = null
    setBrouillon(null)
  }, [ajout])

  function position(e: React.PointerEvent): [number, number] {
    const r = couche.current!.getBoundingClientRect()
    return [
      Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    ]
  }

  function majBrouillon(z: Zone | null) {
    brouillonRef.current = z
    setBrouillon(z)
  }

  return (
    <div
      ref={couche}
      className={`liens-couche${edition ? ' edition' : ''}`}
      style={{
        pointerEvents: ajout ? 'auto' : 'none',
        touchAction: ajout ? 'none' : undefined,
        cursor: ajout ? 'crosshair' : undefined
      }}
      onPointerDown={(e) => {
        if (!ajout || (e.pointerType === 'mouse' && e.button !== 0)) return
        e.stopPropagation()
        doigts.current.add(e.pointerId)
        if (doigts.current.size > 1) {
          // Deuxième doigt : c'est un pincement, pas un rectangle.
          depart.current = null
          majBrouillon(null)
          return
        }
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        depart.current = position(e)
      }}
      onPointerMove={(e) => {
        const d = depart.current
        if (!d || doigts.current.size > 1) return
        const [x, y] = position(e)
        majBrouillon({ x: Math.min(d[0], x), y: Math.min(d[1], y), l: Math.abs(x - d[0]), h: Math.abs(y - d[1]) })
      }}
      onPointerUp={(e) => {
        doigts.current.delete(e.pointerId)
        const b = brouillonRef.current
        depart.current = null
        majBrouillon(null)
        const r = couche.current?.getBoundingClientRect()
        if (b && r && b.l * r.width > 8 && b.h * r.height > 8) tracer(b)
      }}
      onPointerCancel={(e) => {
        doigts.current.delete(e.pointerId)
        depart.current = null
        majBrouillon(null)
      }}
    >
      {liens.map((l) => (
        <button
          key={l.id}
          type="button"
          className={`lien-zone${choisi === l.id ? ' choisi' : ''}${l.source === 'manuel' ? ' manuel' : ''}`}
          style={{
            left: pct(l.zone.x),
            top: pct(l.zone.y),
            width: pct(l.zone.l),
            height: pct(l.zone.h),
            pointerEvents: ajout ? 'none' : 'auto'
          }}
          title={l.libelle ? `Aller à ${l.libelle}` : 'Aller à la page liée'}
          aria-label={l.libelle ? `Aller à ${l.libelle}` : 'Aller à la page liée'}
          // Empêche la souris d'attraper le plan pour le déplacer : le clic
          // doit arriver au lien.
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            if (edition) choisir(l.id === choisi ? null : l.id)
            else suivre(l)
          }}
        >
          {edition && <span className="lien-etiquette">{l.libelle ?? '?'}</span>}
        </button>
      ))}
      {brouillon && (
        <div
          className="lien-brouillon"
          style={{ left: pct(brouillon.x), top: pct(brouillon.y), width: pct(brouillon.l), height: pct(brouillon.h) }}
        />
      )}
      {flash && <div key={flash.cle} className="lien-flash" style={{ left: pct(flash.x), top: pct(flash.y) }} />}
    </div>
  )
}

/** Barre du mode « liens » (administrateur), quand aucun lien n'est choisi. */
export function BarreLiens({
  nombre,
  ajout,
  setAjout,
  terminer
}: {
  nombre: number
  ajout: boolean
  setAjout: (v: boolean) => void
  terminer: () => void
}) {
  return (
    <div className="annot-choix">
      <div style={{ flex: 1, minWidth: 0 }}>
        {ajout ? (
          <strong>Trace un rectangle sur la bulle à relier.</strong>
        ) : (
          <>
            <strong>
              {nombre} lien{nombre > 1 ? 's' : ''} sur cette page
            </strong>
            <span className="code" style={{ marginLeft: 8 }}>
              Touche un lien pour le corriger
            </span>
          </>
        )}
      </div>
      <button
        className={`bouton${ajout ? '' : ' creux'}`}
        style={{ padding: '7px 12px', minWidth: 0, width: 'auto' }}
        onClick={() => setAjout(!ajout)}
      >
        {ajout ? 'Annuler' : '+ Ajouter'}
      </button>
      <button className="fermer-x" onClick={terminer} aria-label="Terminer" title="Terminer">
        ✕
      </button>
    </div>
  )
}

/** Lien choisi (mode « liens ») : où il mène, le corriger ou le supprimer. */
export function BandeauLien({
  lien,
  feuillets,
  changer,
  supprimer,
  ouvrir,
  fermer
}: {
  lien: LienPage
  feuillets: Map<number, Feuillet>
  changer: () => void
  supprimer: () => void
  ouvrir: () => void
  fermer: () => void
}) {
  return (
    <div className="annot-choix" style={{ flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          → {libelle(feuillets.get(lien.cible_page), lien.cible_page)}
        </strong>
        <span className="code">
          {lien.libelle ? `Bulle ${lien.libelle} · ` : ''}p. {lien.cible_page} ·{' '}
          {lien.source === 'auto' ? 'trouvé automatiquement' : 'ajouté à la main'}
        </span>
      </div>
      <button className="bouton creux" style={{ padding: '7px 10px', minWidth: 0, width: 'auto' }} onClick={ouvrir}>
        Ouvrir
      </button>
      <button className="bouton creux" style={{ padding: '7px 10px', minWidth: 0, width: 'auto' }} onClick={changer}>
        Changer
      </button>
      <button className="bouton creux" style={{ padding: '7px 10px', minWidth: 0, width: 'auto' }} onClick={supprimer}>
        Supprimer
      </button>
      <button className="fermer-x" onClick={fermer} aria-label="Fermer">
        ✕
      </button>
    </div>
  )
}

/** Choix de la page visée par un lien, dans l'index du plan. */
export function DialogueCible({
  titre,
  total,
  feuillets,
  actuelle,
  valider,
  annuler
}: {
  titre: string
  total: number
  feuillets: Map<number, Feuillet>
  actuelle?: number
  valider: (page: number) => void
  annuler: () => void
}) {
  const [q, setQ] = useState('')
  const champ = useRef<HTMLInputElement>(null)
  useEffect(() => champ.current?.focus(), [])
  const pages = useMemo(() => {
    const r = q.trim().toLowerCase()
    const toutes = Array.from({ length: total }, (_, i) => i + 1)
    if (!r) return toutes
    return toutes.filter((n) => {
      const f = feuillets.get(n)
      return String(n) === r || (f?.code ?? '').toLowerCase().includes(r) || (f?.titre ?? '').toLowerCase().includes(r)
    })
  }, [q, total, feuillets])

  return (
    <div className="annot-voile" onPointerDown={(e) => e.target === e.currentTarget && annuler()}>
      <div className="annot-dialogue" style={{ maxHeight: '80vh' }}>
        <strong>{titre}</strong>
        <input
          ref={champ}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') annuler()
            if (e.key === 'Enter' && pages.length === 1) valider(pages[0])
          }}
          placeholder="Chercher : A402, coupe, 30…"
          style={{ font: 'inherit', fontSize: 16, border: '1.5px solid var(--line-strong)', padding: 8, background: 'var(--ground)', color: 'var(--ink)' }}
        />
        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0 }}>
          {pages.map((n) => {
            const f = feuillets.get(n)
            return (
              <button
                key={n}
                className="carte bande-nulle"
                style={{
                  padding: '8px 10px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'baseline',
                  textAlign: 'left',
                  outline: n === actuelle ? '2px solid var(--gold)' : undefined
                }}
                onClick={() => valider(n)}
              >
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, fontSize: 13, minWidth: 56 }}>{f?.code ?? '—'}</span>
                <span className="titre" style={{ flex: 1, textTransform: 'uppercase', fontSize: 13 }}>
                  {f?.titre ?? ''}
                </span>
                <span className="code">p. {n}</span>
              </button>
            )
          })}
          {pages.length === 0 && <p className="vide">Aucune page.</p>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="bouton creux" style={{ minWidth: 0, width: 'auto' }} onClick={annuler}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  )
}
